import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import logo from '../../../assets/images/SVG/eduoneLogo.svg';
import ContactAdminModal from '../../common/ContactAdminModal/ContactAdminModal';
import countriesData from '../../../assets/json/countries.json';
import authService from '../../../infrastructure/services/authService';
import AccountSelection from './AccountSelection';
import { validatePhoneNumber } from '../../../infrastructure/utils/validation';

const ResetMobile = () => {
    const navigate = useNavigate();
    const [mobile, setMobile] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isContactOpen, setIsContactOpen] = useState(false);
    const [availableAccounts, setAvailableAccounts] = useState([]);
    const [showAccountSelection, setShowAccountSelection] = useState(false);

    // State for dynamically loaded countries
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);

    // Fetch country flag items from the json file on mount
    useEffect(() => {
        setCountries(countriesData);
        if (countriesData && countriesData.length > 0) {
            setSelectedCountry(countriesData[0]);
        }
    }, []);

    // Calculate max length dynamically based on the selected country
    const maxInputLength = selectedCountry?.code === '+94' ? 10 :
                          selectedCountry?.code === '+91' ? 11 :
                          selectedCountry?.code === '+1' ? 10 :
                          selectedCountry?.code === '+44' ? 11 :
                          selectedCountry?.code === '+61' ? 10 : 15;

    const handleVerify = async (e, selectedUserId = null) => {
        if (e) e.preventDefault();
        const trimmedMobile = mobile.trim();

        if (!trimmedMobile) {
            setError('Mobile number is required');
            return;
        }

        const prefix = selectedCountry ? selectedCountry.code : '+94';
        let cleanNumber = trimmedMobile;
        if (cleanNumber.startsWith('0')) {
            cleanNumber = cleanNumber.substring(1);
        }
        const cleanPrefix = prefix.replace(/\s+/g, '');

        // Validate formatting and length dynamically based on country code
        const validationError = validatePhoneNumber(prefix, cleanNumber);
        if (validationError) {
            setError(validationError);
            return;
        }

        const finalMobile = cleanPrefix + cleanNumber;

        setError('');
        setLoading(true);
        try {
            const result = await authService.forgotPasswordInitiate(finalMobile, selectedUserId);

            if (result?.content?.requiresAccountSelection && result?.content?.accounts) {
                setAvailableAccounts(result.content.accounts);
                setShowAccountSelection(true);
                return;
            }

            // Store target in session/localStorage to display on OTP screen and go to next step
            sessionStorage.setItem('resetTarget', finalMobile);
            sessionStorage.setItem('resetType', 'mobile');
            if (selectedUserId !== null) {
                sessionStorage.setItem('resetUserId', selectedUserId);
            } else {
                sessionStorage.removeItem('resetUserId');
            }
            navigate('/forgot-password/otp');
        } catch (err) {
            setError(err?.message || 'Failed to send OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToLogin = () => {
        sessionStorage.removeItem('resetTarget');
        sessionStorage.removeItem('resetType');
        sessionStorage.removeItem('resetUserId');
        sessionStorage.removeItem('resetOtp');
        navigate('/', { replace: true });
    };

    const handleAccountSelect = (account) => {
        const userId = account.userId || account.id;
        handleVerify(null, userId);
    };

    const handleAccountSelectionBack = () => {
        setShowAccountSelection(false);
        setAvailableAccounts([]);
    };

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '480px',
                my: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'center',
                fontFamily: "'Poppins', sans-serif"
            }}
        >
            {/* Logo */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: { xs: 4, md: 5 }
                }}
            >
                <Box
                    component="img"
                    src={logo}
                    alt="EduOne Logo"
                    sx={{
                        height: '45px',
                        width: 'auto'
                    }}
                />
            </Box>

            {/* Header Text */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                    variant="h2"
                    sx={{
                        fontSize: { xs: '26px', sm: '28px' },
                        fontWeight: 700,
                        color: '#231F20',
                        mb: 1.5,
                        fontFamily: "'Poppins', sans-serif"
                    }}
                >
                    Reset Your Password
                </Typography>
                <Typography
                    sx={{
                        fontSize: '14px',
                        color: '#9B9B9B',
                        lineHeight: 1.6,
                        maxWidth: '400px',
                        mx: 'auto',
                        fontFamily: "'Poppins', sans-serif"
                    }}
                >
                    Enter your registered mobile number, and we'll send you a onetime password (OTP) to reset your password.
                </Typography>
            </Box>

            {/* Form */}
            <Box
                component="form"
                onSubmit={handleVerify}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    width: '100%'
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            width: '100%',
                            alignItems: 'center'
                        }}
                    >
                        {/* Custom Country code selector with real flags (loaded from JSON) */}
                        {selectedCountry && countries.length > 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '1.5px solid #cbd5e1',
                                    borderRadius: '16px',
                                    backgroundColor: '#ffffff',
                                    height: '56px',
                                    minWidth: '110px',
                                    px: 1.5,
                                    justifyContent: 'center',
                                    transition: 'all 0.25s ease',
                                    '&:focus-within': {
                                        borderColor: '#ff502d',
                                        boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                                    }
                                }}
                            >
                                <Select
                                    value={selectedCountry.code}
                                    onChange={(e) => {
                                        const country = countries.find(c => c.code === e.target.value);
                                        if (country) {
                                            setSelectedCountry(country);
                                            setMobile('');
                                            setError('');
                                        }
                                    }}
                                    variant="standard"
                                    disableUnderline
                                    IconComponent={() => (
                                        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ marginLeft: '8px' }}>
                                            <path d="M1 1L5 5L9 1" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        fontFamily: "'Poppins', sans-serif",
                                        fontSize: '15px',
                                        color: '#334155',
                                        fontWeight: 500,
                                        display: 'flex',
                                        alignItems: 'center',
                                        '& .MuiSelect-select': {
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 1,
                                            paddingLeft: '0px !important',
                                            paddingRight: '20px !important',
                                            background: 'none',
                                            '&:focus': {
                                                background: 'none'
                                            }
                                        }
                                    }}
                                >
                                    {countries.map((c) => (
                                        <MenuItem
                                            key={c.code}
                                            value={c.code}
                                            sx={{
                                                fontFamily: "'Poppins', sans-serif",
                                                fontSize: '14px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                py: 1
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={`https://flagcdn.com/w40/${c.iso}.png`}
                                                alt={c.label}
                                                sx={{
                                                    width: '20px',
                                                    height: 'auto',
                                                    borderRadius: '2px',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                                }}
                                            />
                                            <Typography sx={{ fontSize: '14px', fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>
                                                {c.code}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        )}

                        {/* Mobile number input field */}
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                border: '1.5px solid',
                                borderColor: error ? '#e74c3c' : '#cbd5e1',
                                borderRadius: '16px',
                                backgroundColor: '#ffffff',
                                transition: 'all 0.25s ease',
                                height: '56px',
                                '&:focus-within': {
                                    borderColor: '#ff502d',
                                    boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                                }
                            }}
                        >
                            <Box
                                component="input"
                                type="tel"
                                placeholder="Mobile Number"
                                value={mobile}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    setMobile(val.slice(0, maxInputLength));
                                    if (error) setError('');
                                }}
                                maxLength={maxInputLength}
                                sx={{
                                    flex: 1,
                                    padding: '16px',
                                    border: 'none',
                                    background: 'transparent',
                                    fontSize: '15px',
                                    color: '#334155',
                                    outline: 'none',
                                    fontFamily: "'Poppins', sans-serif",
                                    width: '100%'
                                }}
                            />
                        </Box>
                    </Box>
                    {error && (
                        <Typography
                            component="span"
                            sx={{
                                color: '#e74c3c',
                                fontSize: '14px',
                                mt: '4px',
                                fontFamily: "'Poppins', sans-serif"
                            }}
                        >
                            {error}
                        </Typography>
                    )}
                </Box>

                {/* Switcher link to email */}
                <Box>
                    <Typography
                        sx={{
                            fontSize: '14px',
                            color: '#475569',
                            fontFamily: "'Poppins', sans-serif"
                        }}
                    >
                        Forgot Mobile ?{' '}
                        <Box
                            component="span"
                            onClick={() => navigate('/forgot-password/email')}
                            sx={{
                                color: '#ff502d',
                                fontWeight: 500,
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                transition: 'color 0.2s',
                                '&:hover': {
                                    color: '#e04324'
                                }
                            }}
                        >
                            Use Email
                        </Box>
                    </Typography>
                </Box>

                {/* Verify Button */}
                <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !mobile.trim()}
                    sx={{
                        width: '100%',
                        background: '#ff502d',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '16px',
                        py: 2,
                        px: 3,
                        fontSize: '16px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textTransform: 'none',
                        boxShadow: '0 8px 20px -6px rgba(255, 80, 45, 0.4)',
                        fontFamily: "'Poppins', sans-serif",
                        transition: 'all 0.25s ease',
                        '&:hover:not(:disabled)': {
                            background: '#e04324',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 12px 24px -6px rgba(255, 80, 45, 0.5)'
                        },
                        '&:disabled': {
                            background: '#ff502d',
                            opacity: 0.6,
                            color: '#fff',
                            boxShadow: 'none',
                            cursor: 'not-allowed'
                        }
                    }}
                >
                    {loading ? 'Sending OTP...' : 'Verify'}
                </Button>

                {/* Back to Login Link */}
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Typography
                        onClick={handleBackToLogin}
                        sx={{
                            fontSize: '14px',
                            color: '#94a3b8',
                            fontFamily: "'Poppins', sans-serif",
                            cursor: 'pointer',
                            transition: 'color 0.2s',
                            textDecoration: 'underline',
                            '&:hover': {
                                color: '#ff502d'
                            }
                        }}
                    >
                        Back to login
                    </Typography>
                </Box>
            </Box>

            <AccountSelection
                open={showAccountSelection}
                accounts={availableAccounts}
                onSelect={handleAccountSelect}
                onClose={handleAccountSelectionBack}
                loading={loading}
            />

            {/* Support link */}
            <Box sx={{ textAlign: 'center', mt: 5, mb: 1 }}>
                <Typography
                    sx={{
                        fontSize: '14px',
                        color: '#475569',
                        fontFamily: "'Poppins', sans-serif"
                    }}
                >
                    Need help to reset password ?{' '}
                    <Box
                        component="span"
                        onClick={() => setIsContactOpen(true)}
                        sx={{
                            color: '#ff502d',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            '&:hover': {
                                color: '#e04324'
                            }
                        }}
                    >
                        Contact us
                    </Box>
                </Typography>
            </Box>

            {/* Reusable ContactAdminModal */}
            <ContactAdminModal
                isOpen={isContactOpen}
                onClose={() => setIsContactOpen(false)}
            />
        </Box>
    );
};

export default ResetMobile;
